import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  FileText, ChatCircleText, MagnifyingGlass, Handshake, Clock, ShieldCheck, ArrowRight,
} from "@phosphor-icons/react";

const GUIDES = [
  {
    icon: FileText,
    color: "text-blue-600 bg-blue-50",
    title: "Writing a standout CV",
    points: [
      "Keep it to two pages and tailor it to each role.",
      "Lead with achievements and measurable results, not just duties.",
      "Use clear section headings and a clean, consistent layout.",
      "Include relevant keywords from the job description.",
    ],
  },
  {
    icon: ChatCircleText,
    color: "text-emerald-600 bg-emerald-50",
    title: "Acing the interview",
    points: [
      "Research the role and prepare examples using the STAR method.",
      "Have three thoughtful questions ready to ask them.",
      "Dress appropriately and arrive (or log in) five minutes early.",
      "Follow up with a short thank-you message afterwards.",
    ],
  },
  {
    icon: MagnifyingGlass,
    color: "text-violet-600 bg-violet-50",
    title: "Searching smarter",
    points: [
      "Set up saved searches and check back regularly for new roles.",
      "Use specific keywords and filter by location and job type.",
      "Save jobs you like so you can apply while they're live.",
      "Apply early — many roles close as soon as they're filled.",
    ],
  },
  {
    icon: Handshake,
    color: "text-orange-500 bg-orange-50",
    title: "Working with VacancyNetwork",
    points: [
      "Apply directly through us — no third-party sites.",
      "Our team reviews and vets every application personally.",
      "We only share your details with an employer once you're a match.",
      "One profile, thousands of live UK & US vacancies.",
    ],
  },
];

const TIPS = [
  { icon: Clock, title: "Apply within 48 hours", text: "Applications submitted early are far more likely to be reviewed before a role closes." },
  { icon: ShieldCheck, title: "Stay confidential", text: "Your CV is only shared after we've spoken with you and confirmed the role is right." },
  { icon: FileText, title: "Keep your CV ready", text: "Have an up-to-date PDF ready so you can apply the moment you spot the right job." },
];

export default function CareerAdvice() {
  return (
    <div className="App bg-secondary/40">
      <Navbar />

      <section className="bg-navy py-16">
        <div className="mx-auto max-w-5xl px-4 md:px-8">
          <p className="text-sm font-semibold uppercase tracking-[0.25em] text-accent">Career Advice</p>
          <h1 className="mt-3 font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
            Advice to help you land your next role
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/60">
            Practical, no-nonsense guidance from the VacancyNetwork team — from writing a CV that gets noticed
            to nailing the interview and working with us to find the right opportunity.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-14 md:px-8">
        <div className="grid gap-6 md:grid-cols-2">
          {GUIDES.map((g, i) => (
            <div key={i} className="rounded-2xl border border-border bg-card p-6" data-testid={`advice-card-${i}`}>
              <span className={`flex h-12 w-12 items-center justify-center rounded-xl ${g.color}`}>
                <g.icon size={24} weight="fill" />
              </span>
              <h2 className="mt-4 font-heading text-lg font-bold">{g.title}</h2>
              <ul className="mt-3 space-y-2.5">
                {g.points.map((p, j) => (
                  <li key={j} className="flex gap-2 text-sm leading-relaxed text-foreground/80">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" /> {p}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-3">
          {TIPS.map((t, i) => (
            <div key={i} className="rounded-2xl border border-border bg-card p-5">
              <t.icon size={22} weight="fill" className="text-primary" />
              <h3 className="mt-3 font-semibold">{t.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{t.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 rounded-2xl bg-navy p-8 text-center sm:flex-row sm:text-left">
          <div>
            <h3 className="font-heading text-xl font-bold text-white">Ready to find your next role?</h3>
            <p className="mt-1 text-sm text-white/60">Thousands of live UK & US vacancies, updated daily.</p>
          </div>
          <Link to="/jobs">
            <Button className="rounded-full px-8" data-testid="advice-browse-jobs">
              Browse Jobs <ArrowRight size={18} className="ml-1" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
